import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sparkles, Calendar, ExternalLink, Mail } from "lucide-react";

function humanizeDelta(iso) {
  if (!iso) return null;
  const now = new Date();
  const then = new Date(iso);
  const diffMs = then.getTime() - now.getTime();
  if (diffMs <= 0) return "Overdue";
  const mins = Math.round(diffMs / 60000);
  const hours = Math.round(diffMs / 3600000);
  const days = Math.round(diffMs / 86400000);
  if (mins < 60) return `in ${mins}m`;
  if (hours < 24) return `in ${hours}h`;
  if (days < 14) return `in ${days}d`;
  const wks = Math.round(days / 7);
  return `in ${wks}w`;
}

function formatSlot(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AdvisorWidget() {
  const [advisor, setAdvisor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/tenant/advisor")
      .then(({ data }) => { if (alive) setAdvisor(data && data.name ? data : null); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading || !advisor) return null;

  const initials = (advisor.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  const delta = humanizeDelta(advisor.next_session_at);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="group flex items-center gap-2 rounded-full border border-slate-200 bg-white hover:bg-cyan-50/50 hover:border-cyan-300 transition-colors px-2 py-1 pr-3"
          data-testid="advisor-widget-trigger"
          aria-label="Your assigned advisor"
        >
          <div className="relative">
            <div className="h-7 w-7 rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center text-[11px] font-semibold text-white overflow-hidden">
              {advisor.avatar_data ? <img src={advisor.avatar_data} alt="" className="h-full w-full object-cover" /> : initials}
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 border-2 border-white" />
          </div>
          <div className="text-left leading-none hidden sm:block">
            <div className="text-[11px] text-slate-500 font-mono uppercase tracking-widest">Your advisor</div>
            <div className="text-[12.5px] font-semibold text-slate-900 mt-0.5 flex items-center gap-1.5">
              {advisor.name}
              {delta && <span className="text-[10px] font-mono font-normal text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full px-1.5 py-0.5">{delta}</span>}
            </div>
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 overflow-hidden" data-testid="advisor-widget-popover">
        <div className="bg-gradient-to-br from-[#0A1329] to-[#162347] p-4 text-white">
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-cyan-400 to-emerald-400 flex items-center justify-center text-sm font-bold text-slate-900 shrink-0 overflow-hidden">
              {advisor.avatar_data ? <img src={advisor.avatar_data} alt="" className="h-full w-full object-cover" /> : initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="inline-flex items-center gap-1 text-[9.5px] font-mono uppercase tracking-[0.22em] text-cyan-300 mb-1">
                <Sparkles className="h-3 w-3" /> Assigned advisor
              </div>
              <div className="font-display font-semibold text-[15px] leading-tight truncate">{advisor.name}</div>
              {advisor.title && <div className="text-[11.5px] text-slate-300 mt-0.5 truncate">{advisor.title}</div>}
            </div>
          </div>
        </div>
        <div className="p-4 space-y-3">
          {advisor.bio && <p className="text-[12.5px] text-slate-600 leading-relaxed">{advisor.bio}</p>}
          {advisor.focus_areas?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {advisor.focus_areas.map((f) => (
                <span key={f} className="text-[10px] font-mono uppercase tracking-widest bg-slate-100 text-slate-700 border border-slate-200 rounded px-1.5 py-0.5">{f}</span>
              ))}
            </div>
          )}
          <div className="rounded-lg bg-cyan-50 border border-cyan-200 p-3">
            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-cyan-700">
              <Calendar className="h-3 w-3" /> Next session
            </div>
            <div className="mt-1 flex items-baseline justify-between">
              <div className="text-[13px] font-semibold text-slate-900" data-testid="advisor-next-session">{formatSlot(advisor.next_session_at)}</div>
              {delta && <div className="text-[11px] font-mono text-cyan-700">{delta}</div>}
            </div>
          </div>
          <div className="flex flex-col gap-2 pt-1">
            {advisor.calendar_url && (
              <a href={advisor.calendar_url} target="_blank" rel="noreferrer"
                 className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#0A1329] hover:bg-[#162347] text-white text-[12.5px] font-medium py-2 px-3 transition-colors"
                 data-testid="advisor-book-btn">
                <Calendar className="h-3.5 w-3.5" /> Book a session <ExternalLink className="h-3 w-3 opacity-70" />
              </a>
            )}
            {advisor.email && (
              <a href={`mailto:${advisor.email}`}
                 className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-[12.5px] font-medium py-2 px-3 transition-colors"
                 data-testid="advisor-email-btn">
                <Mail className="h-3.5 w-3.5" /> Email {advisor.name.split(" ")[0]}
              </a>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
